
import {
  getProfileUrl,
  getUserFullName,
  getProfileUrlByUsername,
  getDiagramThumbnailUrl,
  getDiagramOpenLink,
} from '~/domain/helpers'
import { UserType } from '~/domain/interfaces/UserTypes'
import MachinationsLightPage from '~/domain/pages/MachinationsLightPage'
import AppActions from '@/domain/constants/AppActions'
import ContactDialog from '~/components/dialogs/ContactDialog.vue'

export default MachinationsLightPage.extend({
  layout: 'communityLayout',
  components: { ContactDialog },
  async asyncData(context) {
    const { store, route, error } = context
    const source = route.query.source

    if (route.params.userAction) {
      await store.dispatch('common/setupPageLight', { context })

      const fileKey = route.params.userAction.split('-').slice(-1)[0] // get diagram fileKey from url
      const response = await store.dispatch(
        AppActions.GET_DIAGRAM_CARD_DETAILS,
        {
          context,
          params: {
            fileKey,
            source,
            additionalInfo: true,
          },
        }
      ) // get diagram obj from backend

      if (!response.diagram) {
        // diagram not found / fileKey not valid:
        error({
          statusCode: 404,
          message: `Diagram not found`,
        })
      }

      return {
        diagram: response.diagram,
        relatedDiagrams: response.relatedDiagrams,
      }
    }
  },

  head() {
    // @ts-ignore
    const pageHead = this.pageHead()
    // @ts-ignore
    const title = this.diagram?.name || ''
    const titleLowerCase = title.toLowerCase()
    const titleTemplate = titleLowerCase.includes('diagram')
      ? '%s | Machinations Community'
      : '%s Diagram | Machinations Community'

    return {
      ...pageHead,
      // @ts-ignore
      title: this.diagram?.name,
      titleTemplate,
      meta: [
        {
          hid: 'og:title',
          name: 'og:title',
          // @ts-ignore
          content: this.diagram?.name,
        },
        {
          hid: 'og:url',
          name: 'og:url',
          content: this.$config?.nuxtBaseURL + this.$route.path,
        },
        {
          hid: 'og:image',
          name: 'og:image',
          property: 'og:image',
          // @ts-ignore
          content: this.thumbnailUrl || '',
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content:
            // @ts-ignore
            `Discover ${this.diagram?.name} diagram on the Machinations Community page.` ||
            'scfe',
        },
        {
          property: 'og:article:published_time',
          // @ts-ignore
          content: this.diagram?.created_date,
        },
        {
          property: 'og:article:published_time',
          // @ts-ignore
          content: this.ownerName,
        },
        {
          property: 'og:article:section',
          content: 'Game Design',
        },
        {
          property: 'og:article:tag',
          // @ts-ignore
          content: this.tagsAsString,
        },
        {
          name: 'url',
          content: this.$config?.nuxtBaseURL + this.$route.path,
        },
        {
          hid: 'description',
          name: 'description',
          content:
            // @ts-ignore
            `Discover ${this.diagram?.name} diagram on the Machinations Community page.` ||
            '',
        },
        {
          name: 'keywords',
          // @ts-ignore
          content: this.tagsAsString,
        },
        {
          name: 'author',
          // @ts-ignore
          content: this.ownerName,
        },
        {
          name: 'designer',
          // @ts-ignore
          content: this.ownerName,
        },
        {
          name: 'copyright',
          // @ts-ignore
          content: this.ownerName,
        },
        {
          name: 'revised',
          // @ts-ignore
          content: this.diagram?.lastEdit,
        },
        {
          name: 'machinations',
          // @ts-ignore
          content: `${this.diagram?.name} ${this.diagram?.fuid}`,
        },
        {
          name: 'subject',
          // @ts-ignore
          content: `Game Design Document for ${this.diagram?.name}`,
        },
        { name: 'abstract', content: 'GDD' },
        { name: 'topic', content: 'Game Design and Balancing' },
        { name: 'summary', content: 'Machinations Game Design and Balancing' },
        { name: 'classification', content: 'Game Design' },
        { name: 'identifier-URL', content: this.$config?.nuxtBaseURL },
        { name: 'directory', content: 'submission' },
        { name: 'category', content: 'game development' },
        { name: 'coverage', content: 'Worldwide' },
        { name: 'distribution', content: 'Global' },
        { name: 'rating', content: 'General' },
        { name: 'revisit-after', content: '7 days' },
        { 'http-equiv': 'Expires', content: '0' },
        { 'http-equiv': 'Pragma', content: 'no-cache' },
        { 'http-equiv': 'Cache-Control', content: 'no-cache' },
        ...pageHead?.meta,
      ],
    }
  },

  data() {
    return {
      shareProviders: ['linkedin', 'twitter', 'facebook'],
      isLoaded: false,
      showDescriptionReadMore: false,
      isDescriptionToggled: false,
      contactDialogVisible: false,
      diagram: {} as any,
      relatedDiagrams: [] as any,
    }
  },

  created() {
    if (!this.$route.params.userAction) {
      this.$router.push(
        getProfileUrlByUsername(this.$route.params.communityUser)
      )
    }
  },

  async fetch() {
    const source = this.$route?.query.source
    const fileKey = this.$route?.params?.userAction?.split('-').slice(-1)[0] // get diagram fileKey from url

    // Get diagram contributors from backend
    await this.$store.dispatch(AppActions.GET_DIAGRAM_CONTRIBUTORS, {
      context: this,
      params: {
        fileKey,
        ownerUsername: this.diagram?.owner?.username,
      },
    })

    // Get diagram data without cache (like/follow)
    await this.$store.dispatch(AppActions.GET_DIAGRAM_DATA_NOCACHE, {
      context: this,
      params: {
        fileKey,
        source,
        additionalInfo: true,
      },
    })
  },

  mounted() {
    this.trackEventOpenDiagram()
    this.$nextTick(() => {
      this.isLoaded = true
    })

    // Check if diagram description needs the 'Read more' functionality
    const diagramDescriptionElement = this.$refs
      ?.diagramDescription as HTMLElement
    this.showDescriptionReadMore =
      diagramDescriptionElement?.scrollHeight >
      diagramDescriptionElement?.clientHeight
  },

  computed: {
    ownerProfileUrl(): string {
      return getProfileUrl(this.diagram.owner)
    },
    ownerName(): string {
      return this.diagram?.owner?.name
    },
    thumbnailUrl(): string {
      return (
        this.$config?.apiBaseURL + getDiagramThumbnailUrl(this.diagram?.fuid)
      )
    },
    tagsAsString(): string {
      return this.diagram?.tags?.length > 0
        ? this.diagram?.tags?.reduce(
            (s: string, tag: any) => `${s}, ${tag.name}`,
            ''
          )
        : ''
    },
    ownerHasRelatedDiagrams(): boolean {
      return this.relatedDiagrams &&
        this.relatedDiagrams.length > 0 &&
        this.diagram &&
        this.diagram.owner
        ? this.relatedDiagrams.every(
            (diagram) =>
              diagram.owner &&
              diagram.owner.username === this.diagram.owner.username
          )
        : false
    },
    isAuthenticated(): boolean {
      return this.$store.getters.userIsAuthenticated
    },

    diagramOpenLink(): string {
      return getDiagramOpenLink(this.diagram?.link)
    },
    isProfileNotCurrentUser(): Boolean {
      const authenticatedUser = this.$store.getters.authenticatedUser

      return !(
        authenticatedUser &&
        authenticatedUser?.username === this.diagram.owner?.username
      )
    },

    userIsFetching() {
      return this.$store.getters.userIsFetching
    },

    forkedUserUrl(): string {
      return getProfileUrl(this.diagram.forkSourceUserInfo)
    },
  },

  methods: {
    getUserName(user: UserType): string {
      return getUserFullName(user)
    },

    trackEventOpenDiagram(): void {
      if (this.isAuthenticated) {
        this.$store.dispatch(AppActions.TRACK_SEGMENT_EVENT, {
          context: this,
          params: {
            name: 'Community - Open Diagram',
            category: 'Community',
            action: 'Open diagram',
            label: 'web',
            misc: [
              { type: 'location', value: 'web' },
              { type: 'diagram', value: this.diagram?.name },
            ],
          },
        })
      }
    },

    forkDiagramPressed(): void {
      this.$store.dispatch(AppActions.TRACK_SEGMENT_EVENT, {
        context: this,
        params: {
          name: 'Community - Fork',
          category: 'Community',
          action: 'Fork',
          label: 'web',
          misc: [
            { type: 'location', value: 'web' },
            { type: 'diagram', value: this.diagram?.name },
          ],
        },
      })
    },

    getContributorProfileUrl(link): string {
      return getProfileUrl(link)
    },

    getDateFromNow() {
      if (this.diagram)
        return this.$formatters.formatDateDiff(this.diagram.lastEdit)
      else return ''
    },

    getFormatterUserRole(user) {
      if (user?.jobTitle) {
        if (user?.companyName) {
          return `${user.jobTitle}, ${user.companyName}`
        } else {
          return user.jobTitle
        }
      } else {
        return user.companyName || ''
      }
    },

    openContactDialog() {
      this.contactDialogVisible = true

      this.$store.dispatch(AppActions.TRACK_SEGMENT_EVENT, {
        context: this,
        params: {
          name: 'Community - Get in touch',
          category: 'Community',
          action: 'Get in touch',
        },
      })
    },
  },
})
